import { Auditorium } from 'components/auditorium/basic'
import { CustomForm } from 'components/form/customForm'
import { FormField } from 'components/form/customFormField'
import { ModeSelection } from 'components/modeSelection'
import { ToggleSwitch } from 'components/toggleSwitch'
import { useLocationForm } from 'hooks/admin/useLocations'
import { UseLocationFormProps } from 'hooks/admin/useLocations/useLocationForm'
import React, { useEffect, useState } from 'react'
import { SeatSellingMode, SeatSellingModes } from 'types/Event'

import { ColorPicker } from '..'
import { FormContent, SectionLabel } from './locationsForm.style'

interface LocationFormProps extends UseLocationFormProps {
    onSuccess?: (event_location_id?: string) => void
    disableButtons?: boolean
    formRef?: React.RefObject<HTMLFormElement>
    eventLocation?: boolean
    eventId?: string
    partnerId?: string
}

export const LocationForm: React.FC<LocationFormProps> = ({
    initialData,
    onClose,
    queryKey = ['locations'],
    onSuccess,
    disableButtons = false,
    formRef,
    eventLocation = false,
    eventId,
    partnerId,
}) => {
    const {
        auditorium,
        gallery,
        setAuditorium,
        setGallery,
        auditoriumMiddleColumn,
        galleryMiddleColumn,
        setAuditoriumMiddleColumn,
        setGalleryMiddleColumn,
        defaultSeatSellingMode,
        setDefaultSeatSellingMode,
        control,
        handleSubmit,
        handleSubmitEventLocation,
        setValue,
        errors,
        loading,
        toggleSeat,
        lastEventLocationIdRef,
    } = useLocationForm({
        initialData,
        queryKey,
        onClose,
        eventId,
        partnerId,
        onSuccess,
    })

    const [showGallery, setShowGallery] = useState(false)

    useEffect(() => {
        if (initialData) {
            setValue('locationName', initialData.name || '')
            setValue('address', initialData.address || '')
            setValue('rows', initialData.auditorium.rows || 0)
            setValue('columns', initialData.auditorium.columns || 0)
            setValue('defaultSeatSellingMode', initialData.default_seat_selling_mode)
            setDefaultSeatSellingMode(initialData.default_seat_selling_mode)
            setAuditorium(initialData.auditorium)
            setAuditoriumMiddleColumn(initialData.auditorium?.middle_column)

            if (initialData.auditorium.gallery) {
                setValue('galleryRows', initialData.auditorium.gallery.rows || 0)
                setValue('galleryColumns', initialData.auditorium.gallery.columns || 0)
                setValue('galleryName', initialData.auditorium.gallery.gallery_name || 'Karzat')
                setGallery(initialData.auditorium.gallery)
                setGalleryMiddleColumn(initialData.auditorium.gallery?.middle_column)
                setShowGallery(true)
            } else {
                setValue('galleryRows', 0)
                setValue('galleryColumns', 0)
                setValue('galleryName', 'Karzat')
                setGallery({ rows: null, columns: null, seats: [] })
                setShowGallery(false)
            }

            setValue('color', initialData.color?.replace(/^#/, '') || 'ba9d31')
        }
    }, [initialData, setValue, setAuditorium, setGallery, setAuditoriumMiddleColumn, setGalleryMiddleColumn, setDefaultSeatSellingMode])

    const onModeChange = (newMode: SeatSellingMode) => {
        setDefaultSeatSellingMode(newMode)
        setValue('defaultSeatSellingMode', newMode)
    }

    const handleToggleGallery = (checked: boolean) => {
        setShowGallery(checked)

        setValue('galleryRows', checked ? gallery.rows ?? 0 : 0)
        setValue('galleryColumns', checked ? gallery.columns ?? 0 : 0)

        setGallery((prev) => ({
            ...prev,
            rows: checked ? prev.rows ?? null : null,
            columns: checked ? prev.columns ?? null : null,
            seats: checked ? prev.seats : [],
        }))
    }

    const handleValueChangeWithValidation = (
        e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>,
        fieldName: string,
        min: number = 1,
        max: number = 200,
    ) => {
        let value = parseInt(e.target.value, 10)

        if (value > max) value = max
        if (value < min) value = min

        setValue(fieldName as any, value)
    }

    return (
        <CustomForm
            handleSubmit={eventLocation ? handleSubmitEventLocation : handleSubmit}
            loading={loading}
            initialData={initialData}
            onClose={onClose}
            hasLine={false}
            onSuccess={() => {
                if (eventLocation && lastEventLocationIdRef.current) {
                    onSuccess?.(lastEventLocationIdRef.current)
                } else {
                    onSuccess?.()
                }
            }}
            disableButtons={disableButtons}
            formRef={formRef}
        >
            <FormField
                name="locationName"
                control={control}
                errors={errors}
                label="Helyszín megnevezése"
                placeholder="Név"
                rules={{
                    required: 'A név megadása kötelező',
                    maxLength: {
                        value: 70,
                        message: 'A név legfeljebb 70 karakter hosszú lehet.',
                    },
                }}
            />
            <ColorPicker name="color" control={control} errors={errors} label="Helyszín azonosító színe" />
            <FormField
                name="address"
                control={control}
                errors={errors}
                label="Helyszín címe"
                placeholder="Cím"
                rules={{
                    required: 'A cím megadása kötelező',
                    maxLength: {
                        value: 200,
                        message: 'A cím legfeljebb 200 karakter hosszú lehet.',
                    },
                }}
            />

            <ModeSelection
                options={[
                    { value: SeatSellingModes.SeatBased, label: 'Nézőtér megadása' },
                    { value: SeatSellingModes.General, label: 'Férőhelyek számának megadása' },
                ]}
                selectedValue={defaultSeatSellingMode}
                onChange={onModeChange}
                buttonClassName="my-button"
                selectedButtonClassName="selected"
            />

            <FormContent mode={defaultSeatSellingMode}>
                {defaultSeatSellingMode === SeatSellingModes.SeatBased && (
                    <>
                        <FormField
                            name="rows"
                            control={control}
                            errors={errors}
                            label="Sorok száma"
                            placeholder="0"
                            rules={{ required: 'A sorok számának megadása kötelező' }}
                            min={1}
                            max={200}
                            step={1}
                            type="number"
                            onChange={(e: any) => {
                                handleValueChangeWithValidation(e, 'rows', 1, 200)
                            }}
                        />

                        <FormField
                            name="columns"
                            control={control}
                            errors={errors}
                            label="Székek száma soronként"
                            placeholder="0"
                            rules={{ required: 'A székek számának megadása kötelező' }}
                            min={1}
                            max={200}
                            step={1}
                            type="number"
                            onChange={(e: any) => {
                                handleValueChangeWithValidation(e, 'columns', 1, 200)
                            }}
                        />
                        <Auditorium
                            rows={auditorium?.rows}
                            columns={auditorium.columns}
                            seats={auditorium.seats}
                            toggleSeat={toggleSeat}
                            middleColumn={auditoriumMiddleColumn}
                            onMiddleColumnChange={setAuditoriumMiddleColumn}
                            name="auditorium"
                        />
                        <ToggleSwitch checked={showGallery} onChange={handleToggleGallery} label="Karzat" />
                    </>
                )}

                {defaultSeatSellingMode === SeatSellingModes.General && (
                    <>
                        <FormField
                            name="totalSeats"
                            control={control}
                            errors={errors}
                            label="Férőhelyek száma"
                            placeholder="0"
                            rules={{
                                required: 'A férőhelyek számának megadása kötelező',
                                min: { value: 0, message: 'A férőhelyek számának 0 vagy nagyobb értéket kell adni' },
                            }}
                            min={0}
                            max={10000}
                            step={1}
                            type="number"
                        />
                    </>
                )}

                {showGallery && defaultSeatSellingMode === SeatSellingModes.SeatBased && (
                    <>
                        <SectionLabel>Karzat</SectionLabel>
                        <FormField
                            name="galleryName"
                            control={control}
                            errors={errors}
                            label="Karzat megnevezése"
                            placeholder="Karzat"
                            rules={{
                                maxLength: {
                                    value: 100,
                                    message: 'A karzat neve legfeljebb 100 karakter hosszú lehet.',
                                },
                            }}
                        />
                        <FormField
                            name="galleryRows"
                            control={control}
                            errors={errors}
                            label="Sorok száma"
                            placeholder="0"
                            rules={{ required: 'A karzat sorainak számát megadni kötelező' }}
                            min={1}
                            max={200}
                            step={1}
                            type="number"
                        />
                        <FormField
                            name="galleryColumns"
                            control={control}
                            errors={errors}
                            label="Székek száma soronként"
                            placeholder="0"
                            rules={{ required: 'A karzat székek számának megadása kötelező' }}
                            min={1}
                            max={200}
                            step={1}
                            type="number"
                        />
                        <Auditorium
                            rows={gallery?.rows}
                            columns={gallery?.columns}
                            seats={gallery?.seats}
                            toggleSeat={toggleSeat}
                            middleColumn={galleryMiddleColumn}
                            onMiddleColumnChange={setGalleryMiddleColumn}
                            name="gallery"
                            isGallery
                        />
                    </>
                )}
            </FormContent>
        </CustomForm>
    )
}
